import * as THREE from "three";

const groupQ = new THREE.Quaternion(), parentQ = new THREE.Quaternion();
const neckQ = new THREE.Quaternion(), headQ = new THREE.Quaternion();
const deltaQ = new THREE.Quaternion(), correctionQ = new THREE.Quaternion();
const neckP = new THREE.Vector3(), headP = new THREE.Vector3();
const right = new THREE.Vector3(), forward = new THREE.Vector3();

/** Relaxed cervical alignment, applied to a fresh sampled pose. Keeps the
 * shoulders/torso/feet intact; hair, face and clothing follow their usual skin.
 * `headRest` is the rig's level-facing orientation in avatar-group space.
 */
export function applyRelaxedPosture(
  group: THREE.Object3D, neck: THREE.Bone, head: THREE.Bone,
  headRest: THREE.Quaternion, weight: number, moving: number,
): void {
  if (weight < 1e-4 || !neck.parent || !head.parent) return;
  group.updateWorldMatrix(true, false);
  group.getWorldQuaternion(groupQ);
  neck.getWorldPosition(neckP); head.getWorldPosition(headP);
  headP.sub(neckP).applyQuaternion(parentQ.copy(groupQ).invert());
  const neckPitch = Math.atan2(-headP.z, headP.y);
  // Retain a small cervical curve and a little more inclination in a walk.
  const target = THREE.MathUtils.degToRad(4 + 6 * THREE.MathUtils.clamp(moving, 0, 1));
  const retract = THREE.MathUtils.clamp(neckPitch - target, 0, THREE.MathUtils.degToRad(14)) * weight;
  head.getWorldQuaternion(headQ);
  deltaQ.copy(groupQ).invert().multiply(headQ).multiply(parentQ.copy(headRest).invert());
  forward.set(0, 0, -1).applyQuaternion(deltaQ);
  const headPitch = Math.atan2(-forward.y, Math.hypot(forward.x, forward.z));
  // Keep some of the clip's nod/breathing variation instead of pinning gaze.
  const lift = THREE.MathUtils.clamp(headPitch, 0, THREE.MathUtils.degToRad(35)) * .8 * weight;
  right.set(1, 0, 0).applyQuaternion(groupQ);
  neck.getWorldQuaternion(neckQ);
  correctionQ.setFromAxisAngle(right, retract);
  neck.parent.getWorldQuaternion(parentQ).invert();
  neck.quaternion.copy(parentQ).multiply(correctionQ).multiply(neckQ).normalize();
  neck.updateWorldMatrix(false, false);
  // Counter-rotate the head against the neck adjustment, then lift its gaze.
  // This prevents a neck retraction from tipping the chin into the air.
  correctionQ.setFromAxisAngle(right, lift);
  head.parent.getWorldQuaternion(parentQ).invert();
  head.quaternion.copy(parentQ).multiply(correctionQ).multiply(headQ).normalize();
}
